interface WeixinJSBridgeLike {
  invoke: (api: string, params: Record<string, never>, callback: () => void) => void;
}

const WECHAT_BRIDGE_TIMEOUT_MS = 500;

const getWeixinJSBridge = (): WeixinJSBridgeLike | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return (window as Window & { WeixinJSBridge?: WeixinJSBridgeLike }).WeixinJSBridge;
};

export const isWeChatWebView = (
  userAgent = typeof navigator === 'undefined' ? '' : (navigator.userAgent ?? ''),
): boolean => userAgent.toLowerCase().includes('micromessenger');

const runPlay = (play: () => Promise<unknown> | unknown): Promise<void> =>
  Promise.resolve(play()).then(() => undefined);

/**
 * 微信内置浏览器只在 `WeixinJSBridge.invoke` 的回调里放行媒体播放。
 * 进房后用户手势已经过期，必须把 `play()` 放进这个回调，而不是等回调结束后再播。
 */
const playInsideWeChatBridge = (play: () => Promise<unknown> | unknown): Promise<void> => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return runPlay(play);
  }

  const playInInvoke = (bridge: WeixinJSBridgeLike): Promise<void> =>
    new Promise((resolve, reject) => {
      let started = false;
      const finish = () => {
        if (started) {
          return;
        }
        started = true;
        void runPlay(play).then(() => resolve(), reject);
      };
      try {
        bridge.invoke('getNetworkType', {}, finish);
      } catch {
        finish();
      }
      window.setTimeout(() => {
        if (!started) {
          finish();
        }
      }, WECHAT_BRIDGE_TIMEOUT_MS);
    });

  const bridge = getWeixinJSBridge();
  if (bridge) {
    return playInInvoke(bridge);
  }

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      document.removeEventListener('WeixinJSBridgeReady', onReady);
      void runPlay(play).then(() => resolve(), reject);
    }, WECHAT_BRIDGE_TIMEOUT_MS);
    const onReady = () => {
      window.clearTimeout(timer);
      document.removeEventListener('WeixinJSBridgeReady', onReady);
      const readyBridge = getWeixinJSBridge();
      if (!readyBridge) {
        void runPlay(play).then(() => resolve(), reject);
        return;
      }
      void playInInvoke(readyBridge).then(resolve, reject);
    };
    document.addEventListener('WeixinJSBridgeReady', onReady);
  });
};

/**
 * 非微信环境按普通自动播放策略播。
 * 微信 WebView 里无手势的 play() 会被拒，且拒绝后再走 JSBridge 可能同时放行页面 video 和 SDK 内部播放器，叠成回声。
 * 所以微信里只在 WeixinJSBridge 回调里播一次。
 */
export const playWithWeChatUnlock = async (play: () => Promise<unknown> | unknown): Promise<void> => {
  if (isWeChatWebView()) {
    await playInsideWeChatBridge(play);
    return;
  }
  await runPlay(play);
};
